export interface NeuronConfig {
  rotationDuration: number; // in seconds
  scaleFactor: number;
  glowIntensity: number;
}

export class NeuronAnimation {
  private config: NeuronConfig = {
    rotationDuration: 20,
    scaleFactor: 1.0,
    glowIntensity: 0.4
  };

  private neuronElement: HTMLElement | null = null;
  private isAnimating: boolean = false;

  init(): void {
    this.neuronElement = document.querySelector('.neuron');
    
    if (!this.neuronElement) {
      console.warn('Neuron element not found');
      return;
    }

    this.setupAnimation();
    this.handleResponsiveAnimation();
    this.startAnimation();
  }

  private setupAnimation(): void {
    if (!this.neuronElement) return;

    // Set CSS custom properties for dynamic control
    this.neuronElement.style.setProperty('--rotation-duration', `${this.config.rotationDuration}s`);
    this.neuronElement.style.setProperty('--scale-factor', `${this.config.scaleFactor}`);
    this.neuronElement.style.setProperty('--glow-intensity', `${this.config.glowIntensity}`);
  }

  private handleResponsiveAnimation(): void {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        // Mobile: slower animation, smaller scale
        this.updateConfig({
          rotationDuration: 30,
          scaleFactor: 0.8,
          glowIntensity: 0.3
        });
      } else {
        // Desktop: normal animation
        this.updateConfig({
          rotationDuration: 20,
          scaleFactor: 1.0,
          glowIntensity: 0.4
        });
      }
    };

    // Initial check
    handleResize(mediaQuery);
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleResize);
  }

  private updateConfig(newConfig: Partial<NeuronConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.setupAnimation();
  }

  private startAnimation(): void {
    if (this.isAnimating || !this.neuronElement) return;
    
    this.isAnimating = true;
    this.neuronElement.classList.add('rotating');
  }

  public pauseAnimation(): void {
    if (!this.isAnimating || !this.neuronElement) return;
    
    this.isAnimating = false;
    this.neuronElement.style.animationPlayState = 'paused';
  }

  public resumeAnimation(): void {
    if (this.isAnimating || !this.neuronElement) return;
    
    this.isAnimating = true;
    this.neuronElement.style.animationPlayState = 'running';
  }

  public setRotationSpeed(duration: number): void {
    this.updateConfig({ rotationDuration: duration });
  }

  public destroy(): void {
    this.pauseAnimation();
    this.neuronElement = null;
    this.isAnimating = false;
  }
}